import { StrictMode, Component, type ErrorInfo, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { TonConnectUIProvider } from '@tonconnect/ui-react'
import { router } from './router'
import { applyLightTheme } from './lib/theme'
import './index.css'
import './tokens.css'

// Telegram WebApp init — expand to full height
if (window.Telegram?.WebApp) {
  window.Telegram.WebApp.expand()
  window.Telegram.WebApp.ready()
  // Disable vertical swipe-to-close gesture (available from Bot API 7.7)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(window.Telegram.WebApp as any).disableVerticalSwipes?.()
}

// Force light theme — overrides Telegram's device-based theme injection.
// Dark theme toggle will be added later via applyDarkTheme().
applyLightTheme()

const MANIFEST_URL =
  import.meta.env.VITE_TONCONNECT_MANIFEST_URL ??
  `${window.location.origin}/tonconnect-manifest.json`

// Bot link for twaReturnUrl — tells TonConnect to return here after wallet action
const BOT_URL = import.meta.env.VITE_TMA_URL ?? 'https://t.me/imago_tonbot'

// Top-level error boundary — catches React render errors and shows a recoverable screen
// instead of a blank/dark page. Especially useful for catching TonConnect init failures.
class AppErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[Imago] App crash:', error, info)
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', minHeight: '100dvh', padding: '24px',
          backgroundColor: '#ffffff', color: '#000000', textAlign: 'center', gap: '16px',
        }}>
          <div style={{ fontSize: '40px' }}>✦</div>
          <p style={{ fontSize: '17px', fontWeight: 600 }}>Something went wrong</p>
          <p style={{ fontSize: '13px', color: '#8e8e93', maxWidth: '280px' }}>
            {this.state.error.message}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '8px', padding: '12px 32px', borderRadius: '14px',
              backgroundColor: '#007aff', color: '#ffffff',
              fontSize: '15px', fontWeight: 600, border: 'none', cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AppErrorBoundary>
      <TonConnectUIProvider
        manifestUrl={MANIFEST_URL}
        actionsConfiguration={{
          twaReturnUrl: BOT_URL as `${string}://${string}`,
        }}
      >
        <RouterProvider router={router} />
      </TonConnectUIProvider>
    </AppErrorBoundary>
  </StrictMode>,
)

// Extend Window type for Telegram WebApp
declare global {
  interface Window {
    Telegram?: {
      WebApp: {
        expand(): void
        ready(): void
        colorScheme: 'light' | 'dark'
        themeParams: Record<string, string>
      }
    }
  }
}
